import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Check, Truck, RotateCcw, ShieldCheck, Stethoscope, FlaskConical, Minus, Plus, Gift, ArrowRight, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import { MaskedLines } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

// Custom caduceus glyph — lucide doesn't ship one and we want the V3.1
// "Dermatologist-developed" chip to read as a medical mark, not a flask.
// API mirrors lucide icons (size, className, strokeWidth) so it drops in.
const Caduceus = ({ size = 24, className = "", strokeWidth = 1.5 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {/* Top orb */}
    <circle cx="12" cy="3.5" r="1.1" />
    {/* Central staff */}
    <path d="M12 4.6V21" />
    {/* Wings */}
    <path d="M12 6.4c-2.2 0-4.3.7-5.8 2 1.6-.5 3.6-.8 5.8-.8" />
    <path d="M12 6.4c2.2 0 4.3.7 5.8 2-1.6-.5-3.6-.8-5.8-.8" />
    {/* Intertwined snakes */}
    <path d="M9 9c2 1.2 2 2.8 0 4s-2 2.8 0 4 2 2.8 0 4" />
    <path d="M15 9c-2 1.2-2 2.8 0 4s2 2.8 0 4-2 2.8 0 4" />
    {/* Snake heads */}
    <path d="M8.8 21h1.2" />
    <path d="M14 21h1.2" />
  </svg>
);

// Mobile/tablet single-image swipeable gallery — Shopify-style PDP pattern.
// Mobile / tablet product image gallery. Two variants:
//   • `default` — classic single-image swap with prev/next arrows + n/N counter
//   • `peek`    — V3.1: native scroll-snap track that lets the next image peek
//                 from the right; thin progress bar replaces the counter and
//                 arrows for a cleaner, higher-CRO swipe affordance.
const MobileSwipeGallery = ({
  gallery,
  aspect = "aspect-[4/5]",
  separator = "/",
  variant = "default",
}) => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [progress, setProgress] = useState(0);
  const trackRef = useRef(null);
  const n = gallery.length;
  const touch = useRef({ startX: 0, dx: 0, active: false });

  const go = (d) => {
    setDir(d);
    setIdx((i) => (i + d + n) % n);
  };

  const onTouchStart = (e) => {
    touch.current = { startX: e.touches[0].clientX, dx: 0, active: true };
  };
  const onTouchMove = (e) => {
    if (!touch.current.active) return;
    touch.current.dx = e.touches[0].clientX - touch.current.startX;
  };
  const onTouchEnd = () => {
    if (!touch.current.active) return;
    const dx = touch.current.dx;
    touch.current.active = false;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  // Peek variant: scroll listener updates the progress bar as the shopper
  // swipes through the snap track, and derives the current index for a11y.
  useEffect(() => {
    if (variant !== "peek") return;
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      const p = max > 0 ? Math.min(1, Math.max(0, el.scrollLeft / max)) : 0;
      setProgress(p);
      const slideW = el.clientWidth * 0.9; // matches CSS 90% snap slide
      setIdx(Math.round(el.scrollLeft / slideW));
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [variant]);

  if (variant === "peek") {
    return (
      <div className="lg:hidden -mx-4" data-testid="mobile-gallery-peek">
        {/* Snap-scroll track. Slides are 90% wide so ~10% of the next image
            bleeds in from the right — clearest possible swipe affordance. */}
        <div
          ref={trackRef}
          className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
          style={{ scrollPaddingLeft: "1rem" }}
        >
          {gallery.map((g, i) => (
            <div
              key={i}
              className="snap-start shrink-0 basis-[90%] pl-4 first:pl-4 last:pr-4"
              data-testid={`mobile-peek-slide-${i}`}
            >
              <div
                className={`relative editorial-frame bg-gradient-to-b from-white to-riversol-sand ${aspect} overflow-hidden`}
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
                {g.label && (
                  <span className="pdp-gallery-tag absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase text-riversol-blue bg-white/85 backdrop-blur px-2.5 py-1">
                    {g.label}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        {/* Hairline progress bar — 2px, fills as you swipe. */}
        <div
          className="mx-4 mt-4 mb-2 h-[2px] bg-neutral-200 overflow-hidden"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax={n - 1}
          aria-valuenow={idx}
          aria-label={`Image ${idx + 1} of ${n}`}
          data-testid="mobile-gallery-progress"
        >
          <div
            className="h-full bg-[#0F2E2E] transition-transform duration-200"
            style={{
              width: `${Math.max(100 / n, progress * 100)}%`,
              transform: `translateX(${progress * (100 - Math.max(100 / n, progress * 100))}%)`,
            }}
          />
        </div>
      </div>
    );
  }

  const g = gallery[idx];
  const slideVariants = {
    enter: (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0.4 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0.4 }),
  };

  return (
    <div className="lg:hidden" data-testid="mobile-gallery">
      <div
        className={`relative group editorial-frame bg-gradient-to-b from-white to-riversol-sand ${aspect} overflow-hidden touch-pan-y select-none`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div
            key={idx}
            custom={dir}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ x: { duration: 0.45, ease: [0.16, 1, 0.3, 1] }, opacity: { duration: 0.2 } }}
            className="absolute inset-0"
          >
            <img
              src={g.src}
              alt={g.alt}
              className="w-full h-full object-cover"
              draggable={false}
            />
            {g.label && (
              <span className="pdp-gallery-tag absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase text-riversol-blue bg-white/85 backdrop-blur px-2.5 py-1">
                {g.label}
              </span>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-center gap-6 mt-4 mb-2">
        <button
          onClick={() => go(-1)}
          data-testid="mobile-gallery-prev"
          aria-label="Previous image"
          className="w-9 h-9 flex items-center justify-center text-neutral-700 hover:text-riversol-blue transition-colors"
        >
          <ChevronLeft size={22} strokeWidth={1.5} />
        </button>
        <span className="font-mono text-sm text-neutral-700 tabular-nums">
          {idx + 1}
          <span className="mx-1 text-neutral-400" aria-hidden="true">{separator}</span>
          {n}
        </span>
        <button
          onClick={() => go(1)}
          data-testid="mobile-gallery-next"
          aria-label="Next image"
          className="w-9 h-9 flex items-center justify-center text-neutral-700 hover:text-riversol-blue transition-colors"
        >
          <ChevronRight size={22} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
};

const Stars = ({ rating }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <Star
        key={i}
        size={15}
        className={i < Math.round(rating) ? "fill-riversol-blue text-riversol-blue" : "text-neutral-300"}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

// PDP3.2 desktop-only gallery with two switchable layouts. Clicking the main
// media toggles between them (a fast A/B preview affordance):
//   • Version 1 — the original stacked layout: hero image + a collapsed 2-per-row
//     tile grid revealed by a "Show more" CTA.
//   • Version 2 — single main image with prev/next arrows + a bottom thumbnail row.
// Arrows and thumbnails stopPropagation so they don't trigger the version toggle.
const V32DesktopGallery = ({ gallery, v33 = false, circleThumbs = false }) => {
  const [version, setVersion] = useState(v33 ? 2 : 1);
  const [showMore, setShowMore] = useState(false);
  const [active, setActive] = useState(0);
  const n = gallery.length;
  const toggleVersion = () => { if (v33) return; setVersion((v) => (v === 1 ? 2 : 1)); };
  const go = (d) => setActive((i) => (i + d + n) % n);
  const tag =
    "pdp-gallery-tag absolute font-mono tracking-[0.2em] uppercase text-riversol-blue bg-white/85 backdrop-blur";

  if (version === 1) {
    return (
      <div className="flex flex-col gap-3" data-testid="v32-gallery-v1">
        <button
          type="button"
          onClick={toggleVersion}
          data-testid="v32-gallery-toggle"
          aria-label="Switch gallery view"
          className="relative group editorial-frame bg-gradient-to-b from-white to-riversol-sand aspect-square block w-full cursor-pointer"
        >
          <img
            src={gallery[0].src}
            alt={gallery[0].alt}
            className="w-full h-full object-cover img-zoom"
          />
          <span className={`${tag} top-5 left-5 text-[10px] px-3 py-1.5`}>
            {gallery[0].label}
          </span>
        </button>

        {!showMore && (
          <button
            type="button"
            onClick={() => setShowMore(true)}
            data-testid="v32-gallery-showmore"
            className="mx-auto mt-1 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-riversol-blue border-b border-riversol-blue/40 pb-1 hover:border-riversol-blue transition-colors"
          >
            Show more
            <Plus size={14} />
          </button>
        )}

        <AnimatePresence initial={false}>
          {showMore && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-2 gap-3" data-testid="gallery-tile-grid">
                {gallery.slice(1).map((g, i) => (
                  <div
                    key={i}
                    data-testid={`gallery-tile-${i + 1}`}
                    className="relative group editorial-frame aspect-square bg-gradient-to-b from-white to-riversol-sand"
                  >
                    <img
                      src={g.src}
                      alt={g.alt}
                      className="w-full h-full object-cover img-zoom"
                    />
                    <span className={`${tag} top-3 left-3 text-[9px] px-2 py-1`}>
                      {g.label}
                    </span>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setShowMore(false)}
                data-testid="v32-gallery-showless"
                className="mx-auto mt-4 flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500 border-b border-neutral-300 pb-1 hover:text-riversol-blue hover:border-riversol-blue transition-colors"
              >
                Show less
                <Minus size={14} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Version 2 — single image + arrows + bottom thumbnails
  const g = gallery[active];
  return (
    <div className={`flex flex-col gap-3 ${v33 ? "lg:h-[calc(100dvh-7rem)]" : ""}`} data-testid="v32-gallery-v2">
      <div
        onClick={v33 ? undefined : toggleVersion}
        data-testid="v32-gallery-toggle"
        className={`relative group editorial-frame bg-gradient-to-b from-white to-riversol-sand overflow-hidden ${v33 ? "flex-1 min-h-0" : "aspect-square cursor-pointer"}`}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={active}
            src={g.src}
            alt={g.alt}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className={`w-full h-full ${v33 ? "object-contain p-4" : "object-cover"}`}
          />
        </AnimatePresence>
        <span className={`${tag} top-5 left-5 text-[10px] px-3 py-1.5`}>
          {g.label}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          data-testid="v32-gallery-prev"
          aria-label="Previous image"
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-neutral-800 hover:bg-white shadow-[0_2px_12px_rgba(0,0,0,0.12)] transition-colors"
        >
          <ChevronLeft size={20} strokeWidth={1.75} />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); go(1); }}
          data-testid="v32-gallery-next"
          aria-label="Next image"
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-neutral-800 hover:bg-white shadow-[0_2px_12px_rgba(0,0,0,0.12)] transition-colors"
        >
          <ChevronRight size={20} strokeWidth={1.75} />
        </button>
        {/*<span className="absolute bottom-5 right-5 font-mono text-[10px] tracking-[0.2em] text-neutral-500 bg-white/60 backdrop-blur px-2 py-1 tabular-nums">
          {String(active + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
        </span>*/}
        {circleThumbs && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-5 pb-[10px] flex items-center gap-3" data-testid="v32-gallery-thumbs-circle">
            {gallery.map((t, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setActive(i); }}
                data-testid={`v32-gallery-thumb-${i}`}
                aria-label={`View ${t.label}`}
                className={`w-14 h-14 rounded-full overflow-hidden bg-white border-2 shadow-[0_2px_12px_rgba(0,0,0,0.12)] transition-all duration-200 ${
                  active === i ? "border-riversol-blue scale-105" : "border-white/90 hover:border-neutral-300"
                }`}
              >
                <img src={t.src} alt={t.alt} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>
      {!circleThumbs && (
      <div className="flex flex-wrap gap-2.5 shrink-0" data-testid="v32-gallery-thumbs">
        {gallery.map((t, i) => (
          <button
            key={i}
            onClick={(e) => { e.stopPropagation(); setActive(i); }}
            data-testid={`v32-gallery-thumb-${i}`}
            aria-label={`View ${t.label}`}
            style={v33 ? { clipPath: "inset(0 round 10px)" } : undefined}
            className={`editorial-frame w-[72px] h-[72px] bg-white border transition-colors duration-200 ${
              active === i ? "border-riversol-blue" : "border-neutral-200 hover:border-neutral-400"
            }`}
          >
            <img src={t.src} alt={t.alt} className="w-full h-full object-cover" />
          </button>
        ))}
      </div>
      )}
    </div>
  );
};


// PDP3.3 — the product facts, kept as the original swipe (one full card + a
// peek of the next), placed inside the buy box under the Skin Analyzer.
const HeroFactsSwipeV33 = ({ facts, solidArrows = false }) => {
  const arrowCls = solidArrows
    ? "w-8 h-8 rounded-full bg-riversol-blue text-white flex items-center justify-center hover:opacity-90 disabled:opacity-25 disabled:pointer-events-none transition-opacity"
    : "w-7 h-7 rounded-full border border-neutral-300 text-neutral-700 flex items-center justify-center hover:border-riversol-blue hover:text-riversol-blue disabled:opacity-30 disabled:pointer-events-none transition-colors";
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setProgress(max > 0 ? el.scrollLeft / max : 0);
      const first = el.children[0];
      const cardW = first ? first.getBoundingClientRect().width : el.clientWidth;
      setActive(Math.min(facts.rows.length - 1, Math.round(el.scrollLeft / cardW)));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    return () => el.removeEventListener("scroll", update);
  }, [facts.rows.length]);

  // Desktop click-drag to scroll (mouse only; touch keeps native swipe).
  // Pointer capture + snap toggling makes the drag reliable and smooth.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let down = false, startX = 0, startScroll = 0;
    const snapTo = () => {
      const w = el.children[0]?.getBoundingClientRect().width || el.clientWidth;
      el.style.scrollBehavior = "smooth";
      el.scrollTo({ left: Math.round(el.scrollLeft / w) * w });
    };
    const onDown = (e) => {
      if (e.pointerType !== "mouse") return;
      down = true; startX = e.clientX; startScroll = el.scrollLeft;
      el.style.scrollSnapType = "none";
      el.style.scrollBehavior = "auto";
      el.classList.add("cursor-grabbing");
      try { el.setPointerCapture(e.pointerId); } catch (_) {}
    };
    const onMove = (e) => {
      if (!down) return;
      el.scrollLeft = startScroll - (e.clientX - startX);
    };
    const onUp = (e) => {
      if (!down) return;
      down = false;
      el.classList.remove("cursor-grabbing");
      el.style.scrollSnapType = "";
      snapTo();
      try { el.releasePointerCapture(e.pointerId); } catch (_) {}
    };
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
    };
  }, []);

  const nudge = (d) => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.children[0]?.getBoundingClientRect().width || el.clientWidth;
    el.scrollBy({ left: d * w, behavior: "smooth" });
  };

  const scrollToAnchor = () => {
    const el = document.querySelector(`[data-testid="${facts.anchor}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="mb-4 border border-riversol-blue/25 bg-riversol-blue/[0.04] rounded-[5px] overflow-hidden" data-testid="hero-product-facts-v33">
      <div className="relative">
        <div
          ref={trackRef}
          className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar cursor-grab select-none"
          style={{ scrollbarWidth: "none" }}
          data-testid="hero-facts-swipe-v33"
        >
          {facts.rows.map((row) => (
            <div
              key={row.label}
              className="snap-start shrink-0 basis-[88%] px-5 py-4 flex flex-col gap-1.5 border-l border-neutral-200 first:border-l-0"
            >
              <span className="text-[12px] font-bold tracking-[0.12em] uppercase text-neutral-900">{row.label}</span>
              {row.link ? (
                <span className="text-[13px] leading-relaxed text-neutral-700">
                  {row.value}{" "}
                  <button
                    type="button"
                    onClick={scrollToAnchor}
                    data-testid="facts-ingredients-link"
                    className="group whitespace-nowrap font-mono text-[10px] font-bold tracking-[0.14em] uppercase text-riversol-blue align-baseline hover:underline"
                  >
                    See the actives
                    <ArrowDown size={11} className="inline-block ml-1 -mb-0.5 group-hover:translate-y-0.5 transition-transform" />
                  </button>
                </span>
              ) : (
                <span className="text-[13px] leading-relaxed text-neutral-700">{row.value}</span>
              )}
            </div>
          ))}
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[#F4F6F8] to-transparent transition-opacity duration-300"
          style={{ opacity: progress > 0.95 ? 0 : 1 }}
        />
      </div>
      <div className="px-5 py-3 flex items-center gap-3 border-t border-neutral-100" data-testid="hero-facts-progress-v33">
        {/*<span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400 shrink-0">
          {String(active + 1).padStart(2, "0")} · {String(facts.rows.length).padStart(2, "0")}
        </span>*/}
        <div className="flex-1 h-[2px] bg-neutral-200 relative overflow-hidden rounded-full">
          <div
            className="absolute inset-y-0 left-0 bg-riversol-blue transition-[width] duration-150 ease-out"
            style={{ width: `${Math.max(15, progress * 100)}%` }}
          />
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button type="button" aria-label="Previous fact" data-testid="hero-facts-prev"
            onClick={() => nudge(-1)} disabled={active === 0}
            className={arrowCls}>
            <ChevronLeft size={15} strokeWidth={solidArrows ? 2.2 : 1.8} />
          </button>
          <button type="button" aria-label="Next fact" data-testid="hero-facts-next"
            onClick={() => nudge(1)} disabled={active >= facts.rows.length - 1}
            className={arrowCls}>
            <ChevronRight size={15} strokeWidth={solidArrows ? 2.2 : 1.8} />
          </button>
        </div>
      </div>
    </div>
  );
};


export const HeroSection = ({ variant, setVariant, qty, setQty }) => {
  const P = useProduct();
  const [active, setActive] = useState(0);
  const [plan, setPlan] = useState(P.defaultPlan);
  const [size, setSize] = useState(P.defaultSize ?? P.sizes?.[0]?.id);
  // V3.1 demo-only: "When to use ☀️🌙" chip is dismissable per session so the
  // client can preview both the "with" and "without" states. Refresh restores.
  const [whenToUseVisible, setWhenToUseVisible] = useState(true);
  const selectedPlan = P.plans?.find((p) => p.id === plan);
  const current = P.gallery[active];
  const variantCols = P.variants.length >= 2 ? "grid-cols-2" : "grid-cols-1";

  return (
    <section className={`max-w-[1600px] mx-auto px-4 lg:px-8 ${P.v34 ? "pt-4 lg:pt-8" : "pt-6 lg:pt-14"} pb-14 lg:pb-24`} data-testid="hero-section">
      {P.breadcrumbs && (
        <nav aria-label="Breadcrumb" className="mb-5 lg:mb-8" data-testid="pdp-breadcrumbs">
          <ol className="flex items-center gap-2 text-[12px] tracking-[0.02em] text-neutral-500">
            {P.breadcrumbs.map((b, i) => (
              <li key={b.label} className="flex items-center gap-2">
                <a href={b.href} className="hover:text-riversol-blue transition-colors" data-testid={`breadcrumb-${i}`}>{b.label}</a>
                <span aria-hidden="true" className="text-neutral-300">/</span>
              </li>
            ))}
            <li aria-current="page" className="text-neutral-900 font-medium" data-testid="breadcrumb-current">{P.titleLines.join(" ")}</li>
          </ol>
        </nav>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
        {/* V3.1 mobile-only title block — sits ABOVE the gallery so shoppers
            see the product name + rating before scrolling through images.
            Desktop keeps the original title inside the buy-box column. */}
        {P.v31 && (
          <div className="lg:hidden order-first" data-testid="v31-mobile-title">
            {P.heroBadge && (
              <span className="inline-flex items-center mb-4 px-4 py-1.5 rounded-[8px] border border-riversol-blue text-riversol-blue font-mono text-[11px] font-semibold tracking-[0.12em] uppercase" data-testid="best-seller-badge">
                {P.heroBadge}
              </span>
            )}
            <h1 className="text-[28px] leading-[1.15] tracking-tight font-semibold text-neutral-900 mb-3">
              <MaskedLines lines={P.titleLines} />
            </h1>
            <div className="flex items-center gap-3">
              <Stars rating={P.rating} />
              <span className="text-[13px] text-neutral-500">
                {P.rating} · {P.reviewCount.toLocaleString()} reviews
              </span>
            </div>
            {!P.v32 && P.routineStep && whenToUseVisible && (
              <button
                type="button"
                onClick={() => setWhenToUseVisible(false)}
                data-testid="when-to-use-chip-mobile"
                title="Tap to hide"
                aria-label={`Use ${P.routineStep.icons.map((i) => i.alt).join(" and ")} — tap to hide`}
                className="mt-3 inline-flex items-center gap-2 pl-3 pr-2.5 h-11 rounded-full border border-neutral-200 active:border-neutral-400 transition-colors"
              >
                <span className="font-mono text-[10px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                  When to use
                </span>
                {P.routineStep.icons.map((ic) => (
                  <img
                    key={ic.alt}
                    src={ic.src}
                    alt={ic.alt}
                    title={ic.alt}
                    className="w-5 h-5 object-contain"
                  />
                ))}
              </button>
            )}
          </div>
        )}

        {/* Gallery */}
        {P.galleryOneSkin ? (
          /* V3 OneSkin-style — desktop: LEFT gallery scrolls (first image + 2-col tile grid),
             RIGHT buy box sticky. Mobile/tablet: single-image swipeable gallery with prev/next
             arrows and a counter (Shopify PDP standard). */
          <div className={`lg:col-span-7 ${P.v33 ? "lg:sticky lg:top-24 self-start" : ""}`}>
            <MobileSwipeGallery
              gallery={P.gallery}
              aspect="aspect-square"
              separator={P.v31 ? "·" : "/"}
              variant={P.v31 ? "peek" : "default"}
            />

            {/* Desktop stacked gallery. PDP3.2 swaps in a two-version
                switchable gallery (click media to toggle). */}
            {P.v32 ? (
              <div className="hidden lg:block">
                <V32DesktopGallery gallery={P.gallery} v33={P.v33} circleThumbs={!!P.v34} />
              </div>
            ) : (
            <div className="hidden lg:flex flex-col gap-3">
            <div className="relative group editorial-frame bg-gradient-to-b from-white to-riversol-sand aspect-square">
              <img
                src={P.gallery[0].src}
                alt={P.gallery[0].alt}
                className="w-full h-full object-cover img-zoom"
              />
              <span className="pdp-gallery-tag absolute top-5 left-5 font-mono text-[10px] tracking-[0.2em] uppercase text-riversol-blue bg-white/85 backdrop-blur px-3 py-1.5">
                {P.gallery[0].label}
              </span>
              {/* V3.1 hides the numeric slide counter on the hero image
                  for a cleaner editorial feel. V2/V3 still show it. */}
              {!P.v31 && (
                <span className="absolute bottom-5 right-5 font-mono text-[10px] tracking-[0.2em] text-neutral-500 bg-white/60 backdrop-blur px-2 py-1">
                  01 / {String(P.gallery.length).padStart(2, "0")}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3" data-testid="gallery-tile-grid">
              {P.gallery.slice(1).map((g, i) => (
                <div
                  key={i}
                  data-testid={`gallery-tile-${i + 1}`}
                  className="relative group editorial-frame aspect-square bg-gradient-to-b from-white to-riversol-sand"
                >
                  <img src={g.src} alt={g.alt} className="w-full h-full object-cover img-zoom" />
                  <span className="pdp-gallery-tag absolute top-3 left-3 font-mono text-[9px] tracking-[0.2em] uppercase text-riversol-blue bg-white/85 backdrop-blur px-2 py-1">
                    {g.label}
                  </span>
                </div>
              ))}
            </div>
            </div>
            )}
          </div>
        ) : P.galleryStickyFirst ? (
          /* V3: single grid row. LEFT col holds sticky first image + 2-col tile grid below it.
             RIGHT col has self-stretch so both columns terminate at the same y — the buy-box scroll
             finishes visually in sync with the tile grid on the left. */
          <div className="lg:col-span-7 flex flex-col gap-3">
            <div className="sticky top-24 z-[5] bg-riversol-paper">
              <div className="relative group editorial-frame bg-gradient-to-b from-white to-riversol-sand h-[calc(100vh-160px)] max-h-[720px]">
                <img
                  src={P.gallery[0].src}
                  alt={P.gallery[0].alt}
                  className="w-full h-full object-cover img-zoom"
                />
                <span className="pdp-gallery-tag absolute top-5 left-5 font-mono text-[10px] tracking-[0.2em] uppercase text-riversol-blue bg-white/85 backdrop-blur px-3 py-1.5">
                  {P.gallery[0].label}
                </span>
                <span className="absolute bottom-5 right-5 font-mono text-[10px] tracking-[0.2em] text-neutral-500 bg-white/60 backdrop-blur px-2 py-1">
                  01 / {String(P.gallery.length).padStart(2, "0")}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 relative z-0" data-testid="gallery-tile-grid">
              {P.gallery.slice(1).map((g, i) => (
                <div
                  key={i}
                  data-testid={`gallery-tile-${i + 1}`}
                  className="relative group editorial-frame aspect-[4/5] bg-gradient-to-b from-white to-riversol-sand"
                >
                  <img src={g.src} alt={g.alt} className="w-full h-full object-cover img-zoom" />
                  <span className="pdp-gallery-tag absolute top-3 left-3 font-mono text-[9px] tracking-[0.2em] uppercase text-riversol-blue bg-white/85 backdrop-blur px-2 py-1">
                    {g.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className={`lg:col-span-7 ${P.plans ? "lg:sticky lg:top-24 self-start" : ""}`}>
            {/* V2 (ugc): mobile / tablet uses the same single-image swipe gallery as V3.
                V1 keeps its original mobile behavior unchanged. */}
            {P.ugc && <MobileSwipeGallery gallery={P.gallery} />}

            <div className={`${P.ugc ? "hidden lg:flex" : "flex"} gap-5`}>
            <div className="flex flex-col gap-3 w-[92px] shrink-0">
              {P.gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`gallery-thumb-${i}`}
                  className={`editorial-frame aspect-[4/5] bg-white border transition-colors duration-200 ${
                    active === i ? "border-riversol-blue" : "border-neutral-200 hover:border-neutral-400"
                  }`}
                >
                  <img src={g.src} alt={g.alt} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div className={`flex-1 relative group editorial-frame bg-gradient-to-b from-white to-riversol-sand ${P.plans ? "h-[calc(100vh-140px)]" : "aspect-[4/5]"}`}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={active}
                  src={current.src}
                  alt={current.alt}
                  initial={{ opacity: 0, scale: 1.03 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
              <span className="pdp-gallery-tag absolute top-5 left-5 font-mono text-[10px] tracking-[0.2em] uppercase text-riversol-blue bg-white/80 backdrop-blur px-3 py-1.5">
                {current.label}
              </span>
              <span className="absolute bottom-5 right-5 font-mono text-[10px] tracking-[0.2em] text-neutral-500">
                {String(active + 1).padStart(2, "0")} / {String(P.gallery.length).padStart(2, "0")}
              </span>
            </div>
            </div>
          </div>
        )}

        {/* Purchase panel */}
        <div className={`lg:col-span-5 pt-2 lg:pt-4 ${P.galleryStickyFirst ? "self-stretch" : ""} ${P.galleryOneSkin && !P.v33 ? "lg:sticky lg:top-24 self-start" : ""}`}>
          <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-5">
            {P.category}
          </div>
          {P.heroBadge && (
            <span className="hidden lg:inline-flex items-center mb-4 px-4 py-1.5 rounded-[8px] border border-riversol-blue text-riversol-blue font-mono text-[11px] font-semibold tracking-[0.12em] uppercase" data-testid="best-seller-badge-desktop">
              {P.heroBadge}
            </span>
          )}
          <h1
            className={`${
              P.v31
                ? "text-3xl leading-[1.2] sm:text-4xl sm:leading-[1.2] lg:text-[2.6rem] lg:leading-[1.2] pb-1 tracking-tight font-semibold text-neutral-900 mb-5 max-lg:hidden"
                : "text-3xl sm:text-4xl lg:text-[3.25rem] leading-[1.02] tracking-tight font-semibold text-neutral-900 mb-5"
            } ${P.titleNoWrap ? "whitespace-nowrap" : ""}`}
          >
            <MaskedLines lines={P.titleLines} />
          </h1>

          <div className={`flex items-center gap-3 flex-wrap mb-7 ${P.v31 ? "max-lg:hidden" : ""}`}>
            <Stars rating={P.rating} />
            <span className="text-sm text-neutral-500">
              {P.rating} · {P.reviewCount.toLocaleString()} reviews
            </span>
            {P.v31 && P.routineStep && whenToUseVisible && (
              <button
                type="button"
                onClick={() => setWhenToUseVisible(false)}
                data-testid="when-to-use-chip"
                title="Click to hide"
                aria-label={`Use ${P.routineStep.icons.map((i) => i.alt).join(" and ")} — click to hide`}
                className="ml-auto flex items-center gap-2 pl-2.5 pr-2 py-1 rounded-full border border-neutral-200 hover:border-neutral-400 transition-colors"
              >
                <span className="font-mono text-[9px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                  When to use
                </span>
                {P.routineStep.icons.map((ic) => (
                  <img
                    key={ic.alt}
                    src={ic.src}
                    alt={ic.alt}
                    title={ic.alt}
                    className="w-[18px] h-[18px] object-contain"
                  />
                ))}
              </button>
            )}
          </div>

          {/* Routine step header — hidden in V3.1 (moved lower, near value props) */}
          {P.routineStep && !P.v31 && (
            <div className="flex items-center gap-4 mb-7 pb-6 border-b border-neutral-200" data-testid="routine-step-header">
              <span className="font-mono text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-900">
                {P.routineStep.label}
              </span>
              <span className="flex items-center gap-2 text-[11px] font-mono tracking-[0.12em] uppercase text-neutral-500">
                When to use:
                {P.routineStep.icons.map((ic) => (
                  <img key={ic.alt} src={ic.src} alt={ic.alt} title={ic.alt} className="w-6 h-6 object-contain" />
                ))}
              </span>
            </div>
          )}

          <p className={`leading-relaxed mb-8 max-w-lg ${P.plans ? "text-lg text-neutral-800" : "text-[15px] text-neutral-600"}`}>
            {P.shortDescription}
          </p>

          {P.sizes && (
            <div className="mb-8 pt-6 border-t border-neutral-300" data-testid="size-selector">
              <div className="text-[14px] font-semibold text-riversol-blue mb-2.5">Size</div>
              <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Choose a size">
                {P.sizes.map((s) => {
                  const isSel = size === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      role="radio"
                      aria-checked={isSel}
                      onClick={() => setSize(s.id)}
                      data-testid={`size-${s.id}`}
                      className={`min-w-[132px] px-5 py-2.5 rounded-[2px] border text-[14px] font-medium text-center transition-colors duration-200 ${
                        isSel ? "border-riversol-blue bg-riversol-blue text-white" : "border-riversol-blue text-riversol-blue bg-white hover:bg-riversol-blue/[0.06]"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {P.plans ? (
            <>
              {/* Purchase plan selector */}
              <div className="mb-5 space-y-3" role="radiogroup" aria-label="Choose your purchase option" data-testid="plan-selector">
                {P.plans.map((p) => {
                  const isSel = plan === p.id;
                  const ribbon = p.ribbon;
                  return (
                    <div
                      key={p.id}
                      className={`border rounded-sm transition-all duration-200 overflow-hidden ${
                        isSel ? "border-riversol-blue bg-riversol-blue/[0.035] shadow-[0_2px_16px_rgba(11,37,69,0.06)]" : ribbon ? "border-riversol-blue/40 hover:border-riversol-blue" : "border-neutral-200 hover:border-neutral-400"
                      }`}
                    >
                      {ribbon && (
                        <div className="flex items-center justify-center bg-riversol-blue text-white py-2 px-4" data-testid="plan-ribbon-membership">
                          <span className="font-mono text-[10px] font-semibold tracking-[0.22em] uppercase">{p.ribbon}</span>
                        </div>
                      )}
                      <button
                        role="radio"
                        aria-checked={isSel}
                        aria-expanded={isSel}
                        onClick={() => setPlan(p.id)}
                        data-testid={`plan-${p.id}`}
                        className="w-full text-left px-5 py-4 flex items-start gap-4"
                      >
                        <span className={`mt-0.5 shrink-0 w-[18px] h-[18px] rounded-full border flex items-center justify-center transition-colors ${isSel ? "border-riversol-blue" : "border-neutral-300"}`}>
                          {isSel && <span className="w-2.5 h-2.5 rounded-full bg-riversol-blue" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-neutral-900">{p.title}</span>
                            {p.badge && !ribbon && (
                              <span className={`font-mono text-[9px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-full ${p.id === "membership" ? "bg-riversol-blue text-white" : "bg-riversol-blue/10 text-riversol-blue"}`}>
                                {p.badge}
                              </span>
                            )}
                          </span>
                          <span className="block text-xs text-neutral-500 mt-1 leading-snug">{p.subtitle}</span>
                        </span>
                        <span className="text-right shrink-0">
                          {p.compareAt && <span className={`block text-sm text-neutral-500 line-through leading-none mb-1 ${P.v31 ? "" : "font-mono"}`}>{p.compareAt}</span>}
                          <span className={`block text-lg text-neutral-900 leading-none ${P.v31 ? "font-semibold" : "font-mono"}`}>{p.price}</span>
                          <span className={`block text-[10px] text-neutral-400 mt-1 ${P.v31 ? "" : "font-mono"}`}>{p.unit}</span>
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {(isSel || (P.v34 && p.id === "membership")) && p.benefits && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className="overflow-hidden"
                          >
                            <div className={`px-5 pb-5 ${P.v34 ? "" : "pl-[54px]"}`}>
                              <ul className="space-y-2 pt-1" data-testid={`plan-benefits-${p.id}`}>
                                {p.benefits.map((b, bi) => (
                                  <li key={bi} className="flex items-start gap-2.5 text-[13px] text-neutral-600 leading-snug">
                                    <Check size={14} className="text-riversol-blue mt-0.5 shrink-0" strokeWidth={2.5} />
                                    {b}
                                  </li>
                                ))}
                              </ul>
                              {/* Free-gift rows shown only for plans that ship
                                  gifts (Riversol+ Membership). Mirrors the
                                  47skin pattern: thumbnail on the left,
                                  bold "+ Free X" title + fine-print reveal
                                  order + "FREE" pill on the right. */}
                              {p.gifts && (
                                <ul
                                  className="mt-4 space-y-2"
                                  data-testid={`plan-gifts-${p.id}`}
                                >
                                  {p.gifts.map((g, gi) => (
                                    <li
                                      key={gi}
                                      className="flex items-center gap-3 bg-white border border-neutral-200 px-3 py-2.5 rounded-sm"
                                      data-testid={`plan-gift-${p.id}-${gi}`}
                                    >
                                      <img
                                        src={g.image}
                                        alt=""
                                        className="w-11 h-11 object-cover bg-riversol-sand shrink-0"
                                        loading="lazy"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-medium text-neutral-900 leading-tight truncate">
                                          {g.label}
                                        </div>
                                        <div className="text-[12px] text-neutral-500 leading-tight mt-0.5 truncate">
                                          {g.meta}
                                        </div>
                                      </div>
                                      <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-white bg-riversol-blue px-2 py-1 rounded-sm shrink-0">
                                        FREE
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {p.learnMore && (
                                <a
                                  href={p.learnMore.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`plan-learnmore-${p.id}`}
                                  className="group mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.16em] uppercase text-riversol-blue border-b border-riversol-blue/30 hover:border-riversol-blue pb-0.5 transition-colors"
                                >
                                  {p.learnMore.label}
                                  <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                                </a>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>

              {/* Anniversary offer — V2/V3 render it above the CTA; V3.1
                  moves it BELOW the CTA (see block after `perOrder`) with
                  click-to-toggle expanded/compact modes. */}
              {P.anniversaryOffer && !P.v31 && (
                <div className="flex items-center gap-3.5 mb-4 px-5 py-4 border border-riversol-blue/25 bg-riversol-blue/[0.04] rounded-sm" data-testid="anniversary-offer">
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-riversol-blue/10 shrink-0">
                    <Gift size={19} className="text-riversol-blue" />
                  </span>
                  <p className="text-sm text-neutral-700 leading-snug">
                    <span className="font-semibold text-riversol-blue">{P.anniversaryOffer.label}:</span> {P.anniversaryOffer.text}{" "}
                    <span className="font-mono font-semibold tracking-[0.08em] text-neutral-900 border border-dashed border-riversol-blue/40 px-2 py-0.5 rounded-sm">{P.anniversaryOffer.code}</span>
                  </p>
                </div>
              )}

              {/* Skin Analyzer CTA — default position (above Add to Cart).
                  In V3.1 this block is moved BELOW the CTA (rendered later). */}
              {!P.v31 && (
                <a
                  href="https://www.riversol.com/pages/skin-analyzer"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="skin-analyzer-cta"
                  className="group flex items-center gap-4 mb-7 pr-4 border border-neutral-200 bg-white rounded-sm overflow-hidden hover:border-riversol-blue transition-colors"
                >
                  <div className="w-[92px] h-[92px] shrink-0 overflow-hidden bg-riversol-sand">
                    <img
                      src="//www.riversol.com/cdn/shop/files/cori-analyzer-cta_sm.png?v=1774307255&width=200"
                      alt="Riversol Skin Analyzer"
                      loading="lazy"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: "49.121% 51.66%" }}
                    />
                  </div>
                  <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                    <div className="flex flex-col">
                      <span className="text-[15px] font-semibold text-neutral-900 leading-tight">Riversol Skin Analyzer</span>
                      <span className="text-[13px] text-neutral-500 mt-0.5 leading-snug">Find your perfect routine</span>
                    </div>
                    <span className="text-riversol-blue shrink-0 group-hover:translate-x-1 transition-transform" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </div>
                </a>
              )}
            </>
          ) : (
            <>
              {/* Price */}
              <div className="flex items-end gap-4 mb-2">
                <span className="text-3xl font-mono text-neutral-900">{P.price.regular}</span>
                <span className="text-sm text-neutral-400 mb-1">USD</span>
              </div>
              <div className="flex items-center gap-2 mb-8">
                <span className="font-mono text-sm text-riversol-blue">{P.price.member}</span>
                <span className="text-xs text-neutral-500">{P.promo}</span>
              </div>

              {/* Variants */}
              <div className="mb-8">
                <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-500 mb-3">
                  Size — <span className="text-neutral-900">{P.variants.find((v) => v.id === variant)?.label}</span>
                </div>
                <div className={`grid ${variantCols} gap-3`}>
                  {P.variants.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setVariant(v.id)}
                      data-testid={`variant-${v.id}`}
                      className={`text-left px-5 py-4 border transition-all duration-200 ${
                        variant === v.id
                          ? "border-riversol-blue bg-riversol-blue/[0.03]"
                          : "border-neutral-200 hover:border-neutral-400"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-neutral-900">{v.label}</span>
                        {variant === v.id && <Check size={15} className="text-riversol-blue" />}
                      </div>
                      <div className="text-xs text-neutral-500 mt-1">{v.detail}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Quantity + CTA */}
          <div className="flex items-stretch gap-3 mb-4">
            <div className={`flex items-center border border-neutral-200 ${P.plans ? (P.v34 ? "overflow-hidden" : P.v33 ? "rounded-[5px] overflow-hidden" : P.v32 ? "overflow-hidden" : "rounded-full overflow-hidden") : ""}`}>
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrement" aria-label="Decrease quantity" className="pl-5 pr-3 h-full text-neutral-600 hover:text-riversol-blue transition-colors"><Minus size={15} /></button>
              <span className="w-8 text-center font-mono text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(3, qty + 1))} data-testid="qty-increment" aria-label="Increase quantity" className="pr-5 pl-3 h-full text-neutral-600 hover:text-riversol-blue transition-colors"><Plus size={15} /></button>
            </div>
            <button
              data-testid="hero-shop-now"
              className={`flex-1 min-w-0 bg-riversol-blue text-white text-xs sm:text-sm tracking-[0.06em] sm:tracking-[0.12em] uppercase font-medium py-3.5 sm:py-4 px-3 hover:bg-riversol-blueLight transition-colors duration-200 ${P.plans ? (P.v34 ? "" : P.v33 ? "rounded-[5px]" : P.v32 ? "" : "rounded-full") : ""}`}
            >
              {selectedPlan ? (
                <span className="inline-flex items-center gap-1.5 sm:gap-2 whitespace-nowrap">
                  <span className="hidden sm:inline">{selectedPlan.cta}</span>
                  <span className="sm:hidden">Add to cart</span>
                  <span className="hidden sm:inline">-</span>
                  {selectedPlan.compareAt && <span className="hidden sm:inline line-through opacity-60 font-normal">{selectedPlan.compareAt}</span>}
                  <span>{selectedPlan.price}</span>
                </span>
              ) : P.ctaLabel}
            </button>
          </div>
          {P.perOrder && <p className="text-xs text-neutral-400 mb-8">{P.perOrder}</p>}
          {P.memberNudge && (
            <p className="text-[15px] text-neutral-700 mb-8 -mt-1" data-testid="member-nudge">
              {P.memberNudge.text} <strong className="font-semibold text-neutral-900">{P.memberNudge.brand}</strong>.{" "}
              <a href={P.memberNudge.signUpHref} className="underline underline-offset-4 decoration-neutral-400 hover:text-riversol-blue hover:decoration-riversol-blue transition-colors" data-testid="member-nudge-signup">{P.memberNudge.signUpLabel}</a>
              {" "}{P.memberNudge.or}{" "}
              <a href={P.memberNudge.signInHref} className="underline underline-offset-4 decoration-neutral-400 hover:text-riversol-blue hover:decoration-riversol-blue transition-colors" data-testid="member-nudge-signin">{P.memberNudge.signInLabel}</a>
            </p>
          )}

          {/* V3.1: Anniversary Offer sits BELOW the CTA as a compact
              one-line pill — icon + inline label · text · code chip,
              quiet chrome to match the V3.1 aesthetic without competing
              with the primary CTA. */}
          {/*{P.v31 && P.anniversaryOffer && (
            <div
              data-testid="anniversary-offer"
              className={`w-full mb-8 px-4 py-2.5 border border-riversol-blue/25 bg-riversol-blue/[0.04] ${P.v33 ? "rounded-[10px]" : P.v32 ? "" : "rounded-full"}`}
            >
              <div className="flex items-center gap-3">
                <Gift size={16} className="text-riversol-blue shrink-0" />
                <p className="text-[13px] text-neutral-700 leading-none truncate flex items-center gap-2 flex-1">
                  <span className="font-semibold text-riversol-blue">{P.anniversaryOffer.label}</span>
                  <span className="text-neutral-400">·</span>
                  <span className="hidden sm:inline">{P.anniversaryOffer.text}</span>
                  <span className="sm:hidden">$38 off $75+</span>
                  <span className="font-mono font-semibold tracking-[0.08em] text-neutral-900 border border-dashed border-riversol-blue/40 px-1.5 py-0.5 rounded-sm text-[11px]">
                    {P.anniversaryOffer.code}
                  </span>
                </p>
              </div>
            </div>
          )}*/}

          {/* V3.1 — Skin Analyzer moved BELOW the primary CTA */}
          {P.v31 && !P.v33 && (
            <a
              href="https://www.riversol.com/pages/skin-analyzer"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="skin-analyzer-cta"
              className="group flex items-center gap-4 mb-8 pr-4 border border-neutral-200 bg-white rounded-sm overflow-hidden hover:border-riversol-blue transition-colors"
            >
              <div className="w-[92px] h-[92px] shrink-0 overflow-hidden bg-riversol-sand">
                <img
                  src="//www.riversol.com/cdn/shop/files/cori-analyzer-cta_sm.png?v=1774307255&width=200"
                  alt="Riversol Skin Analyzer"
                  loading="lazy"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "49.121% 51.66%" }}
                />
              </div>
              <div className="flex-1 flex items-center justify-between gap-2 min-w-0">
                <div className="flex flex-col">
                  <span className="text-[15px] font-semibold text-neutral-900 leading-tight">Riversol Skin Analyzer</span>
                  <span className="text-[13px] text-neutral-500 mt-0.5 leading-snug">Find your perfect routine</span>
                </div>
                <span className="text-riversol-blue shrink-0 group-hover:translate-x-1 transition-transform" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
            </a>
          )}

          {/* PDP3.3 — Skin Analyzer as a compact full-image-background banner
              (HP4 abstract art direction), scoped strictly to v33. Soft dark
              wash keeps the current copy legible; pops without feeling heavy. */}
          {P.v34 && P.facts && <HeroFactsSwipeV33 facts={P.facts} solidArrows />}
          {P.v33 && (
            <a
              href="https://www.riversol.com/pages/skin-analyzer"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="skin-analyzer-cta"
              className="group relative flex items-center mb-4 overflow-hidden rounded-[5px] min-h-[168px]"
            >
              <img
                src="/media/s_analyzer.png"
                alt="Riversol Skin Analyzer"
                loading="lazy"
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {!P.v34 && <div className="absolute inset-0 bg-[#8B857C]/35" />}
              <div className={`absolute inset-0 bg-gradient-to-r ${P.v34 ? "from-black/60 via-black/25 via-[42%] to-transparent to-[68%]" : "from-black/60 via-black/25 to-transparent"}`} />
              <div className="relative z-10 flex items-center justify-between w-full px-7 py-6 gap-3">
                <div className="flex flex-col">
                  <span className="text-[20px] font-semibold text-white leading-tight tracking-[-0.01em]">Riversol Skin Analyzer</span>
                  <span className="text-[13px] text-white/85 mt-1.5 leading-snug">Find your perfect routine</span>
                </div>
                <span className="text-white shrink-0 group-hover:translate-x-1 transition-transform" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 3.75L13.75 10L7.5 16.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
            </a>
          )}

          {/* PDP3.3 — product facts (Targets / Suited to …) as a peek-swipe,
              directly under the Skin Analyzer block, on the above-fold buy box. */}
          {P.v33 && !P.v34 && P.facts && <HeroFactsSwipeV33 facts={P.facts} />}

          {/* V3.1 "When to use ☀️🌙" chip moved into the rating row above.
              Clicking it hides for the current session (demo behaviour). */}

          {/* Reassurance */}
          <div className={`grid grid-cols-3 gap-4 ${P.v33 ? "pt-1 pb-6" : "py-6"} ${P.v31 ? "" : "border-t border-neutral-200"}`}>
            {[
              { icon: Truck, text: "Free shipping over $69" },
              { icon: RotateCcw, text: "30 day returns" },
              // V3.1 gets a caduceus (medical mark) for the dermatology guarantee
              { icon: P.v31 ? Caduceus : ShieldCheck, text: "Dermatologist-developed" },
            ].map(({ icon: Icon, text }) => (
              <div
                key={text}
                className={`flex gap-2 ${P.v31 ? "flex-col items-center text-center" : "flex-col items-start"}`}
              >
                <Icon size={P.v31 ? 22 : 18} className="text-riversol-blue" strokeWidth={1.5} />
                <span className={`text-[11px] leading-snug ${P.v31 ? "text-neutral-600" : "text-neutral-500"}`}>{text}</span>
              </div>
            ))}
          </div>
          {P.whenToUse && !P.v31 && (
            <p className="text-xs text-neutral-500 font-mono mt-4">{P.whenToUse}</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
